import { useState, useCallback, useRef, useEffect } from 'react';
import SmartySDK from 'smartystreets-javascript-sdk';
import { SmartyStreetsAutocompleteSuggestion } from './types';

const SmartyCore = SmartySDK.core;
const AutocompletePro = SmartySDK.usAutocompletePro;

type SmartyAutocompleteClient = SmartySDK.core.Client<
  SmartySDK.usAutocompletePro.Lookup,
  SmartySDK.usAutocompletePro.Lookup
>;

interface UseSmartyStreetsAutocompleteOptions {
  apiKey: string;
  /**
   * Debounce delay in milliseconds
   * @default 300
   */
  debounceMs?: number;
  /**
   * Maximum number of suggestions to return
   * @default 10
   */
  maxSuggestions?: number;
}

export function useSmartyStreetsAutocomplete(
  options: UseSmartyStreetsAutocompleteOptions
) {
  const { apiKey, debounceMs = 300, maxSuggestions = 10 } = options;

  const [suggestions, setSuggestions] = useState<
    SmartyStreetsAutocompleteSuggestion[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const clientRef = useRef<SmartyAutocompleteClient | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize the SmartyStreets client
  useEffect(() => {
    const credentials = new SmartyCore.SharedCredentials(apiKey);
    const clientBuilder = new SmartyCore.ClientBuilder(credentials);
    clientRef.current = clientBuilder.buildUsAutocompleteProClient();

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [apiKey]);

  const search = useCallback(
    async (searchText: string, selectedAddress?: string) => {
      if (!searchText || searchText.length < 3) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const lookup = new AutocompletePro.Lookup(searchText);
        lookup.maxResults = maxSuggestions;

        // Support secondary expansion via selected parameter
        if (selectedAddress) {
          lookup.selected = selectedAddress;
        }

        const response = await clientRef.current.send(lookup);

        if (response.result && Array.isArray(response.result)) {
          setSuggestions(response.result);
        } else {
          setSuggestions([]);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Autocomplete failed'));
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [maxSuggestions]
  );

  const debouncedSearch = useCallback(
    (searchText: string, selectedAddress?: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      if (!searchText || searchText.length < 3) {
        setSuggestions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      debounceTimerRef.current = setTimeout(() => {
        search(searchText, selectedAddress);
      }, debounceMs);
    },
    [search, debounceMs]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setError(null);
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    search: debouncedSearch,
    searchWithSelected: search, // Immediate search for secondary expansion (no debounce)
    clearSuggestions,
  };
}
